import type { GameState, PowerUpKind } from '@pacmates/engine';
import { constants } from '@pacmates/engine';
import { FRUIT_TIER_COLOR, FRUIT_TIER_NAME, pacColor, POWERUP_COLOR, POWERUP_LABEL } from './colors';

export interface HUDProps {
  state: GameState;
  p1Name: string;
  p2Name: string;
  p2Connected: boolean;
  /** False in a SOLO round: the engine's `GameState.players` is always a
   * fixed 2-tuple (a solo round still simulates a P2 pac parked at spawn),
   * so this controls DISPLAY only -- P2 is "real" iff `localTwoPlayer`
   * (same-device 2P) or a networked player has actually joined seat 2 (see
   * GameScreen.tsx's `p2Active` derivation). Reactive: flips live the
   * instant a networked player fills seat 2 mid-game, since it's plain
   * props, not local state. */
  showP2: boolean;
  /** PART 2: which seat THIS client is -- the active-buff row only ever
   * shows the LOCAL player's own `powerups` map (freeze/scorex2 are
   * team-wide in effect, but still stored per-player by the engine -- see
   * activeBuffs() below -- so reading the other seat's copy would just
   * duplicate the same team-wide timers, never show anything unique). */
  mySeat: 1 | 2;
  /** PART 3: colorblind-safe palette toggle -- the HUD badges/legend read
   * through `pacColor()` (same as the canvas renderer) so they never drift
   * out of sync with what's actually drawn in the maze. Defaults to false
   * so any other/older caller keeps the default palette. */
  colorblind?: boolean;
}

export interface ActiveBuff {
  kind: PowerUpKind;
  /** Whole seconds remaining (ceil'd so a live countdown never flashes
   * "0s" for a full second before actually expiring). */
  seconds: number;
  /** freeze/scorex2 apply to BOTH players at once (constants.ts
   * isGlobalPowerup) -- flagged so the pill can say so instead of reading
   * as a solo buff only the local player benefits from. */
  team: boolean;
}

/** Pure derivation of the HUD's active-buff pills off one player's
 * `powerups` map (SPEC.md §1.1: read straight off the authoritative
 * snapshot, no client-side timer bookkeeping). Exported so it's directly
 * unit-testable without mounting the HUD. */
export function activeBuffs(powerups: Partial<Record<PowerUpKind, number>>): ActiveBuff[] {
  return (Object.keys(powerups) as PowerUpKind[])
    .filter((k) => (powerups[k] ?? 0) > 0)
    .sort()
    .map((k) => ({ kind: k, seconds: Math.ceil(powerups[k]!), team: constants.isGlobalPowerup(k) }));
}

/** Live HUD: both players' scores/lives, level, fruit tray. Every number
 * here is read directly off the authoritative snapshot -- no client-side
 * scorekeeping (SPEC.md §1.1). In a solo round (`showP2=false`) only the P1
 * badge + center info render -- no stray P2 badge for the engine's
 * parked-at-spawn second pac. */
export function HUD({ state, p1Name, p2Name, p2Connected, showP2, mySeat, colorblind = false }: HUDProps): JSX.Element {
  const [p1, p2] = state.players;
  const trayCount = Math.min(state.level, 7);
  const trayTiers = Array.from({ length: trayCount }, (_, i) => constants.fruitTierIndex(state.level - i));
  const myPlayer = state.players[mySeat - 1]!;
  const buffs = activeBuffs(myPlayer.powerups);

  return (
    <>
      <div className={`hud ${showP2 ? 'hud--duo' : 'hud--solo'}`} role="status" aria-label="Game status">
        <PlayerBadge label="P1" name={p1Name} color={pacColor(1, colorblind)} score={p1!.score} lives={p1!.lives} connected />
        <div className="hud-center">
          <div className="hud-level">Level {state.level}</div>
          <div className="hud-fruit-tray" aria-label="Fruit tray">
            {trayTiers.map((t, i) => (
              <span
                key={i}
                className="hud-fruit-icon"
                style={{ background: FRUIT_TIER_COLOR[t] }}
                title={FRUIT_TIER_NAME[t]}
              />
            ))}
          </div>
          <div className="hud-dots">{state.pelletsRemaining} dots left</div>
        </div>
        {showP2 && (
          <PlayerBadge
            label="P2"
            name={p2Name}
            color={pacColor(2, colorblind)}
            score={p2!.score}
            lives={p2!.lives}
            connected={p2Connected}
            // PART 3: non-hue marker so P2 reads as distinct even under
            // colorblind mode's own P1/P2 palette (same idea as the
            // canvas's ring marker on the P2 pac -- see renderEntities.ts).
            markerRing={colorblind}
          />
        )}
      </div>
      {/* Rendered only while >=1 buff is active -- an empty box would both
          steal maze-canvas budget (this row is a flow child measured by
          useGameLayout's `topStackEl` ResizeObserver) and look like a
          layout glitch the rest of the time. */}
      {buffs.length > 0 && (
        <div className="hud-buff-row" aria-label="Active power-ups">
          {buffs.map((b) => (
            <span
              key={b.kind}
              className={`hud-buff-pill${b.team ? ' hud-buff-pill--team' : ''}`}
              style={{ borderColor: POWERUP_COLOR[b.kind] }}
            >
              {b.team && <span className="hud-buff-team-tag">TEAM</span>}
              {b.team ? ' ' : ''}
              {POWERUP_LABEL[b.kind] ?? b.kind.toUpperCase()} {b.seconds}s
            </span>
          ))}
        </div>
      )}
    </>
  );
}

function PlayerBadge({
  label,
  name,
  color,
  score,
  lives,
  connected,
  markerRing = false,
}: {
  label: string;
  name: string;
  color: string;
  score: number;
  lives: number;
  connected: boolean;
  /** PART 3: draws a light ring AROUND the color dot (not just the flat
   * fill) -- a non-hue cue distinguishing this badge from the other seat's
   * even under a form of color blindness that flattens both hexes toward
   * the same perceived color. */
  markerRing?: boolean;
}): JSX.Element {
  return (
    <div
      className={`hud-badge${!connected ? ' hud-badge--offline' : ''}`}
      style={{ borderColor: color }}
      data-testid={`hud-badge-${label.toLowerCase()}`}
    >
      <div className="hud-badge-top">
        <span
          className={`hud-dot${markerRing ? ' hud-dot--ring' : ''}`}
          style={{ background: color }}
          aria-hidden
        />
        <span className="hud-badge-name">
          {label}: {name || '(empty seat)'}
        </span>
        {!connected && (
          // DEFECT 10 fix: visible "OFFLINE" text, not a bare "!" glyph
          // that only explained itself via a hover-only `title` tooltip.
          <span className="hud-offline-icon" title="Disconnected">
            OFFLINE
          </span>
        )}
      </div>
      <div className="hud-badge-score">{score.toLocaleString()}</div>
      <div className="hud-badge-lives" role="img" aria-label={`${lives} lives`}>
        {Array.from({ length: Math.max(0, lives) }, (_, i) => (
          <span key={i} className="hud-life-icon" style={{ background: color }} />
        ))}
      </div>
    </div>
  );
}
